<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";
import CrewCollapsible from "@/components/CrewCollapsible/CrewCollapsible.vue";
import { useRoute, useRouter } from 'vue-router';
import { CrewMember } from "@/models/CrewMember";
import { useCrewStore } from '@/stores/crewStore';
import { vMaska } from "maska/vue";
import { getAllCountries } from "@/services/country.service";
import FileUpload from "@/components/FileUpload/FileUpload.vue";
import { useWindowSize } from "@vueuse/core";
import { useDocumentHandler } from '@/composables/useDocumentHandler';
import { useValidationRules } from '@/composables/useValidationRules';
import * as DateUtils from "@/helpers/dateUtils";
import {useUserStore} from "@/stores/userStore";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {VueSpinner} from "vue3-spinners";

const i18n = useI18n({ useScope: "global" });
const { t,locale } = useI18n();
const { passportExpiryDateRule, passportNumberRule } = useValidationRules();

// Make passport validations optional (unchanged from previous step)
const optionalPassportNumberRule = (v: string) => {
  if (v == null || String(v).trim() === '') return true;
  return passportNumberRule(v);
};
const optionalpassportExpiryDateRule = (v: string) => {
  if (v == null || String(v).trim() === '') return true;
  return passportExpiryDateRule(v);
};

const router = useRouter();
const route = useRoute();
const countries = ref([]);
const crewStore = useCrewStore();
const isLoading = ref(true);
const userStore = useUserStore();

const dateInputConfig = computed(() =>
    DateUtils.getDateMaskAndPlaceholder(locale.value)
);

watch(
    () => userStore.sessionRestored,
    async (restored) => {
      if (restored) {
        try {
          isLoading.value = true;

          if (!userStore.user) {
            await userStore.fetchUserProfile();
          }

          const response = await getAllCountries();
          countries.value = response.data._embedded?.countries || [];
          await crewStore.fetchCrewList(route.params.id as string);

        } finally {
          isLoading.value = false;
        }
      }
    },
    {immediate: true}
);

const isDragging = ref(false);
const toggleValue = ref(false);
const vhfToggleValue = ref(false);
const licenseInputRef = ref(null);
const vhflicenseInputRef = ref(null);
const passportInputRef = ref(null);
const showAddNewCrewList = ref(false);

const formRef = ref();
const isFormValid = ref(false);
const isSaving = ref(false);

const submitError = ref<string | null>(null);

const hiddenPassportCount = ref(0);


function resetNewCrewForm() {
  newCrewMember.value = {
    firstName: '',
    lastName: '',
    email: '',
    nationality: null,
    phone: '',
    passportNumber: '',
    passportExpiryDate: '',
    birthDate: '',
  };

  pendingPassportFiles.value = [];
  pendingLicenseFiles.value = [];
  pendingVHFFiles.value = [];
  hiddenPassportCount.value = 0;

  toggleValue.value = false;
  vhfToggleValue.value = false;
  isDragging.value = false;

  formRef.value?.reset?.();
  formRef.value?.resetValidation?.();
}

function handleCloseModal() {
  showAddNewCrewList.value = false;
  resetNewCrewForm();
}

async function validateAndSubmit() {
  if (isSaving.value) return;
  submitError.value = null; // clear any previous error before validating
  const result = await formRef.value?.validate?.();
  const isValid = typeof result === 'boolean' ? result : result?.valid;
  if (!isValid) return;
  try {
    isSaving.value = true;
    await onSubmit();
  } finally {
    isSaving.value = false;
  }
}

const {
  passportUploadError,
  licenseUploadError,
  vhfLicenseUploadError,
  pendingPassportFiles,
  pendingLicenseFiles,
  pendingVHFFiles,
  validateAndProcessFile,
  convertFilesToBase64Array,
  handlePendingFileInput,
} = useDocumentHandler();

hiddenPassportCount.value = pendingPassportFiles.value.length;

watch(pendingPassportFiles, (files) => {
  hiddenPassportCount.value = files.length;
  formRef.value?.validate?.();
});

function navigateToCrewList() {
  router.push(`/my/crew-list`);
}

const newCrewMember = ref<Partial<CrewMember & { lastName: string }>>({
  firstName: '',
  lastName: '',
  email: '',
  nationality: null,
  phone: '',
  passportNumber: '',
  passportExpiryDate: '',
  birthDate: '',
});

function triggerLicenseUpload() {
  licenseInputRef.value?.click();
}

function triggerVhfLicenseUpload() {
  vhflicenseInputRef.value?.click();
}

function triggerPassportUpload() {
  passportInputRef.value?.click();
}

async function onSubmit() {
  const payload: any = {
    ...newCrewMember.value,
      name : newCrewMember.value.firstName +" "+newCrewMember.value.lastName,
      birthDate : DateUtils.toApiDate(newCrewMember.value.birthDate, i18n.locale.value),
      passportExpiryDate : DateUtils.toApiDate(newCrewMember.value.passportExpiryDate, i18n.locale.value)
  };

  if (pendingPassportFiles.value.length)
    payload.passportDocuments = await convertFilesToBase64Array(pendingPassportFiles.value);

  if (pendingLicenseFiles.value.length)
    payload.licenseDocuments = await convertFilesToBase64Array(pendingLicenseFiles.value);

  if (pendingVHFFiles.value.length)
    payload.vhfLicenseDocuments = await convertFilesToBase64Array(pendingVHFFiles.value);

  try {
    await crewStore.saveCrewMember(route.params.id as string, payload);
    await crewStore.fetchCrewList(route.params.id as string);
    console.log("line 194 crew details");
    submitError.value = null;
    showAddNewCrewList.value = false;

    newCrewMember.value = {
      firstName: '',
      lastName: '',
      email: '',
      nationality: {"name":'', "id":null},
      phone: '',
      passportNumber: '',
      passportExpiryDate: '',
      birthDate: '',
    };
    pendingPassportFiles.value = [];
    pendingLicenseFiles.value = [];
    pendingVHFFiles.value = [];
    toggleValue.value = false;
    vhfToggleValue.value = false;
    hiddenPassportCount.value = 0;
  } catch (err: any) {
    console.error('Failed to create crew member:', err);
    submitError.value =
      err?.response?.data?.message ||
      err?.message ||
      'Failed to create crew member. Please try again.';
  }
}

watch(showAddNewCrewList, (newValue) => {
  document.documentElement.style.overflow = newValue ? 'hidden' : 'auto';
});

const { width } = useWindowSize();
const isMobile = computed(() => width.value <= 500);
</script>

<template>

    <div class="container flex flex-col gap-8 py-12" v-if="crewStore.status === 'success'">
        <div class="flex justify-between align-center">
          <div class="flex gap-2 responsive-title-3 font-semibold header" @click="navigateToCrewList()">
            <img src="@/assets/icons/svg/chevron.svg" alt="Crew">
            <h1>{{ crewStore.crewList.listName }}</h1>
          </div>
            <div v-if="crewStore.crewList && crewStore.crewList.length !== 0 && !isMobile">
                <CustomButton
                    type="black"
                    class="self-end  responsive-body-medium w-fit h-8"
                    :outlined="false"
                    :rounded="true"
                    @click="showAddNewCrewList = true">
                    {{ t('crewList2.addNewMember') }}
                </CustomButton>
            </div>
        </div>
        <div v-if="isLoading" class="flex justify-center items-center py-12">
          <CustomSpinner/>
        </div>
        <div class="flex justify-center items-center h-full gap-5 min-h-[400px] alignBoxClass"
            v-if="crewStore.crewList && crewStore.crewList.members && crewStore.crewList.members.length === 0">
            <div class="flex flex-col bg-background-light boxClass justify-between rounded-8xl p-7.5">
                <div>
                    <img src="@/assets/icons/svg/Crew_40x40.svg" alt="Crew">
                </div>
                <div class=" responsive-body-large font-semibold">{{ t('crewList2.noCrewMember') }}</div>
            </div>
            <div @click="showAddNewCrewList = true"
                class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-8xl p-12 gap-1 cursor-pointer boxClass desktop"
                type="primary">
                <img src="@/assets/icons/svg/Add_24x24_white.svg" alt="Plus" width="44" />
                <div class=" responsive-body-large font-semibold text-center">{{ t('crewList2.addNewMember') }}</div>
            </div>
        </div>
        <div class="flex flex-col gap-7" v-else-if="crewStore.crewList && crewStore.crewList.members && crewStore.crewList.members.length !== 0">
            <div class="flex flex-col gap-4 ">
              <div v-if="!isLoading">
                <div v-for="(crew) in crewStore.crewList.members" :key="crew.uuid" class="">
                    <CrewCollapsible :member="crew" :loading="isLoading"/>
                </div>
                <div class="flex justify-center items-center w-full mt-4" v-if="crewStore.crewList && crewStore.crewList.length !== 0 && isMobile">
                  <CustomButton
                      type="black"
                      class="w-full h-8 responsive-body-medium"
                      :outlined="false"
                      :rounded="true"
                      @click="showAddNewCrewList = true">
                    {{ t('crewList2.addNewMember') }}
                  </CustomButton>
                </div>
              </div>
              <div v-else class="w-1/2 flex flex-col gap-8 py-12 mainContainer">
                <div class="w-fit px-5 py-2 rounded-22xl">
                  <CustomSpinner/>
                </div>
              </div>
            </div>
        </div>
    </div>

    <CustomModal :show="showAddNewCrewList" :custom-index="40" :mobile-fullscreen="true" @on-close="handleCloseModal" classes="w-1/6 sm:w-[400px]" extra-classes="h-fit" drawerHeight="fit-content">
        <VForm
          ref="formRef"
          v-model="isFormValid"
          validate-on="input"
          class="flex flex-col h-screen py-12 min-h-0 sm:h-auto md:max-h-[85vh] sm:p-0 md:p-5 container"
          @submit.prevent="validateAndSubmit"
        >

          <div
            class="cursor-pointer self-end sticky top-0 z-10 bg-white pb-4"
            @click="handleCloseModal"
            role="button"
            aria-label="Close"
          >
            <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
          </div>


          <VAlert
            v-if="submitError"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-2"
          >
            {{ submitError }}
          </VAlert>
          <div class="flex-1 min-h-0 flex flex-col gap-8 px-2 pb-5 sm:px-9 sm:py-7.5 overflow-y-auto overflow-x-hidden ios-touch-scroll">
                <div class="flex flex-col pb-2">
                    <div class=" responsive-title-4 font-semibold">{{ t('crewList2.addMemberTitle') }}</div>
                </div>
                <div class="bg-notifications-pending px-5 py-2.5 rounded-8xl">
                    <span class=" responsive-body-small font-semibold">{{ t('crewList2.infoNotice') }}</span>
                </div>
                <div class="flex flex-col">
                    <div class="flex gap-4 rowClass">
                        <VTextField
                    :label="`${t('crewList2.form.firstName')} *`"
                    v-model="newCrewMember.firstName"
                    variant="underlined"
                    :rules="[v => !!v || t('profile.firstNameRequired')]"
                  />
                  <VTextField
                    :label="`${t('crewList2.form.lastName')} *`"
                    v-model="newCrewMember.lastName"
                    variant="underlined"
                    :rules="[v => !!v || t('profile.lastNameRequired')]"
                  />
                </div>
                <div class="flex gap-4 rowClass">
                  <VTextField
                    :label="`${t('profile.email')} *`"
                    type="email"
                    variant="underlined"
                    placeholder="you@example.com"
                    :rules="[
                      v => !!v || t('profile.emailRequired'),
                      v => /.+@.+\..+/.test(v) || t('profile.emailInvalid')
                    ]"
                    v-model="newCrewMember.email"
                  />
                  <VTextField
                    :label="`${t('profile.phoneNumber')} *`"
                    type="phone"
                    variant="underlined"
                    v-model="newCrewMember.phone"
                    :rules="[v => !!v || t('profile.phoneRequired')]"
                  />
                </div>
                <div class="flex gap-4 rowClass">
                  <VSelect
                    :label="`${t('profile.nationality')} *`"
                    variant="underlined"
                    v-model="newCrewMember.nationality"
                    item-title="name"
                    item-value="id"
                    :items="countries"
                    :return-object="false"
                    :rules="[v => !!v || t('profile.nationalityRequired')]"
                    class="w-full sm:w-1/2"
                  />
                  <VTextField
                    class="w-full sm:w-1/2"
                    :label="`${t('profile.birthdate')} *`"
                    type="text"
                    variant="underlined"
                    v-maska="dateInputConfig.mask"
                    :placeholder="dateInputConfig.placeholder"
                    v-model="newCrewMember.birthDate"
                    :rules="[v => !!(v && String(v).trim()) || t('profile.birthdateRequired')]"
                  />
                </div>
                </div>
                  <div class="my-2">
                    <span class="responsive-body-medium">{{ t('profile.passportPhoto') }}</span>
                    <FileUpload
                        label="profile.uploadLicenseFiles"
                        type="passport"
                        :uploadFiles="pendingPassportFiles"
                        :uploadError="passportUploadError"
                        :isDragging="isDragging"
                        :onUploadTrigger="triggerPassportUpload"
                        :onDrop="(e) => handlePendingFileInput(e, 'passport')"
                        :onFileChange="(e) => handlePendingFileInput(e, 'passport')"
                        :onRemove="(index) => pendingPassportFiles.splice(index, 1)"
                    />
                    <div class="flex gap-4 rowClass">
                      <div class="flex-1 min-w-0">
                        <VTextField
                            :label="t('profile.passportNumber')"
                            type="pass_number"
                            variant="underlined"
                            v-model="newCrewMember.passportNumber"
                            :rules="[optionalPassportNumberRule]"
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <VTextField
                            :label="t('profile.passportExpiryDate')"
                            type="pass_date"
                            variant="underlined"
                            v-maska="dateInputConfig.mask"
                            :placeholder="dateInputConfig.placeholder"
                            v-model="newCrewMember.passportExpiryDate"
                            :rules="[optionalpassportExpiryDateRule]"
                            hint=" "
                            persistent-hint
                        />
                      </div>
                  </div>
                  <div class="my-4">
                    <div class="responsive-body-medium align-center flex gap-6 ml-5">
                      <VSwitch
                          v-model="toggleValue"
                          inset
                          base-color="grey"
                          color="black"
                          hide-details
                          density="compact"
                      />
                      {{ t('profile.sailingLicense') }}
                    </div>
                    <FileUpload
                        v-if="toggleValue"
                        label="profile.uploadLicenseFiles"
                        type="license"
                        :uploadFiles="pendingLicenseFiles"
                        :uploadError="licenseUploadError"
                        :onUploadTrigger="triggerLicenseUpload"
                        :onDrop="(e) => handlePendingFileInput(e, 'license')"
                        :onFileChange="(e) => handlePendingFileInput(e, 'license')"
                        :onRemove="(index) => pendingLicenseFiles.splice(index, 1)"
                    />
                  </div>
                  <div class="my-2">
                    <div class="responsive-body-medium align-center flex gap-6 ml-5">
                      <VSwitch
                          v-model="vhfToggleValue"
                          inset
                          base-color="grey"
                          color="black"
                          hide-details
                          density="compact"
                      />
                      {{ t('profile.vhfLicense') }}
                    </div>
                    <FileUpload
                        v-if="vhfToggleValue"
                        label="profile.uploadLicenseFiles"
                        type="vhf"
                        :uploadFiles="pendingVHFFiles"
                        :uploadError="vhfLicenseUploadError"
                        :isDragging="isDragging"
                        :onUploadTrigger="triggerVhfLicenseUpload"
                        :onDrop="(e) => handlePendingFileInput(e, 'vhf')"
                        :onFileChange="(e) => handlePendingFileInput(e, 'vhf')"
                        :onRemove="(index) => pendingVHFFiles.splice(index, 1)"
                    />
                  </div>

                </div>
        <div class="flex justify-between align-center">
          <CustomButton
            type="black"
            class="self-start responsive-body-medium "
            :rounded="true"
            :outlined="false"
            size="medium"
            :disabled="!isFormValid || isSaving"
            @click="validateAndSubmit"
          >
            <div class="flex items-center gap-2">
              <VueSpinner v-if="isSaving" :size="16" color="white"/>
              <span>{{ t('crewList2.buttons.addMember') }}</span>
            </div>
          </CustomButton>
        </div>
            </div>
        </VForm>
      <VInput
          v-model="hiddenPassportCount"
          class="hidden"
      />
    </CustomModal>

</template>

<style scoped>
.semiboldText {
    color: #8C8B94;
}

.modal{
  z-index: 999;
}

.boxClass {
    min-width: 200px;
    max-width: 220px;
    min-height: 240px;
    max-height: 260px;
}

.rowScroll {
    overflow-x: scroll;
}

@media (min-width: 768px) {
    .phone {
        display: none;
    }
}

@media (max-width: 768px) {
    .desktop {
        display: none;
    }

    .boxClass {
        min-width: 150px;
        max-width: 180px;
        min-height: 180px;
        max-height: 200px;
    }

    .rowScroll {
        overflow: unset !important;
    }
}

 :deep(.v-switch__track) {
   opacity: 1;
 }
 :deep(.v-input__details) {
  min-height: 20px;
  padding-top: 2px;
}
.semiboldText {
  color: #8C8B94;
}

.imageProfile {
  border: 1px solid #B7B6BE;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: scale-down;
}

.imageProfile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 50%;
  display: block;
}

/* For smaller screens */
@media (max-width: 768px) {
  .imageProfile {
    width: 100px;
    height: 100px;
    object-fit: scale-down;
  }
}

@media (max-width: 400px) {
  .imageProfile {
    width: 50%;
    height: 50%;
    object-fit: scale-down;
  }
}

.el-overlay{
  z-index: 90;
}

.customBoxClass {
  border: 1px solid #EEEEF0;
  border-radius: 10px;
}

.customBorder {
  border-bottom: 1px solid #EEEEF0;
}

.progress {
  border-bottom: 1px solid #9BE7BA;
}

.drag-active {
  border: 2px dashed #1B84FF;
  background-color: #f0f8ff;
}

.rowClass {
  margin-bottom: -5px;
}


.rowClass > * {
  flex: 1 1 0;
  min-width: 0;
}

:deep(.v-field) {
  transition: none !important;
  box-sizing: border-box;
}

:deep(.v-input__details) {
  min-height: 20px;
  padding-top: 2px;
}

:deep(.v-alert) {
  flex:inherit !important;
}
@media (max-width:768px) {
  .mainContainer {
    width: 100%;
    padding: 37px 20px;
  }

  .rowClass {
    flex-direction: column;
    gap: 0px;
  }

  .rmMobile {
    display: none;
  }

  .photoPass {
    flex-direction: row-reverse;
  }

  .licensesClass {
    flex-direction: column;
    gap: 10px;
  }

  .licensesClass>div,
  .licensesClass>button {
    align-self: start;
  }
}
</style>
